import { LayerCard, Text } from '@cloudflare/kumo'
import { WifiHighIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from '../../i18n'
import { CredentialRow } from './CredentialRow'

export interface WifiNetwork {
  name: string
  securityType: string
  vlanId: number
  password: string | null
  credential?: { username: string; password: string | null } | null
}

export function CredentialsCard({
  network,
  revealed,
  onToggleReveal,
}: {
  network: WifiNetwork
  /** Reveal state is owned by the page so setup steps mask the same way. */
  revealed: boolean
  onToggleReveal: () => void
}) {
  const { t } = useTranslation()
  const enterprise = network.securityType === 'WPA2/WPA3 Enterprise' && network.credential
  const password = enterprise ? network.credential!.password : network.password

  return (
    <LayerCard>
      <LayerCard.Secondary>
        <div className="flex items-center gap-2">
          <WifiHighIcon size={16} className="text-kumo-subtle" />
          <Text as="span" variant="secondary">
            {network.securityType} &middot; VLAN {network.vlanId}
          </Text>
        </div>
      </LayerCard.Secondary>
      <LayerCard.Primary>
        <div className="flex flex-col">
          <CredentialRow label={t('wifi.network')} value={network.name} />
          {enterprise && (
            <CredentialRow label={t('wifi.username')} value={network.credential!.username} />
          )}
          {/* Enterprise login without a password yet: say so, as before ("—"). */}
          {enterprise && !password && (
            <div className="flex flex-col" style={{ padding: '8px 0', minHeight: 56 }}>
              <Text size="sm" variant="secondary">
                {t('wifi.password')}
              </Text>
              <Text>—</Text>
            </div>
          )}
          {password && (
            <CredentialRow
              label={t('wifi.password')}
              value={password}
              secret
              revealed={revealed}
              onToggleReveal={onToggleReveal}
            />
          )}
        </div>
      </LayerCard.Primary>
    </LayerCard>
  )
}

export function useRevealState() {
  const [revealed, setRevealed] = useState(false)
  return [revealed, () => setRevealed((r) => !r)] as const
}
